import type {
  ContatoCorretorGrupo,
  ContatosDetalheFiltros,
  LigacaoRelatorioRow,
} from '../../types/comercialContatosPortal'

export type { ContatoCorretorGrupo, ContatosDetalheFiltros }

function compareLigacaoDesc(a: LigacaoRelatorioRow, b: LigacaoRelatorioRow): number {
  const d = String(b.data).localeCompare(String(a.data))
  if (d !== 0) return d
  const t = String(b.horario).localeCompare(String(a.horario))
  if (t !== 0) return t
  return String(b.id).localeCompare(String(a.id))
}

function pickResumo(contatos: LigacaoRelatorioRow[]): {
  resumo: LigacaoRelatorioRow
  resumoOrigem: ContatoCorretorGrupo['resumoOrigem']
} {
  const comSucesso = contatos.filter(c => c.teve_contato)
  if (comSucesso.length > 0) {
    return { resumo: comSucesso[0], resumoOrigem: 'ultimo_sucesso' }
  }
  return { resumo: contatos[0], resumoOrigem: 'ultimo_contato' }
}

function hasDetalheFiltros(filtros?: ContatosDetalheFiltros): boolean {
  if (!filtros) return false
  return Boolean(
    filtros.call_direction ||
      filtros.contact_channel ||
      filtros.contact_made !== undefined ||
      filtros.commercial_contact_reason_id
  )
}

export function matchesContatoDetalhe(
  row: LigacaoRelatorioRow,
  filtros?: ContatosDetalheFiltros
): boolean {
  if (!filtros || !hasDetalheFiltros(filtros)) return true
  if (filtros.call_direction && row.tipo !== filtros.call_direction) return false
  if (filtros.contact_channel && row.tipo_contato !== filtros.contact_channel) return false
  if (filtros.contact_made === true && !row.teve_contato) return false
  if (filtros.contact_made === false && row.teve_contato) return false
  if (
    filtros.commercial_contact_reason_id &&
    row.commercial_contact_reason_id !== filtros.commercial_contact_reason_id
  ) {
    return false
  }
  return true
}

/**
 * Agrupa contatos por corretora.
 * - `contatos`: histórico completo do período (não corta por meio/tipo/sucesso/motivo).
 * - Listagem: só inclui corretora se houver ao menos um contato que bata nos filtros de detalhe.
 * - `resumo`: último com sucesso (senão o mais recente) entre os contatos que batem o detalhe;
 *   sem filtros de detalhe, considera todos.
 */
export function groupLigacoesByCorretor(
  rows: LigacaoRelatorioRow[],
  detalheFiltros?: ContatosDetalheFiltros
): ContatoCorretorGrupo[] {
  const byCorretor = new Map<string, LigacaoRelatorioRow[]>()

  for (const row of rows) {
    const key = row.corretor_id || row.id
    const list = byCorretor.get(key)
    if (list) list.push(row)
    else byCorretor.set(key, [row])
  }

  const grupos: ContatoCorretorGrupo[] = []
  for (const [corretor_id, contatosRaw] of byCorretor) {
    const contatos = [...contatosRaw].sort(compareLigacaoDesc)
    const candidatosResumo = hasDetalheFiltros(detalheFiltros)
      ? contatos.filter(c => matchesContatoDetalhe(c, detalheFiltros))
      : contatos

    if (candidatosResumo.length === 0) continue

    const { resumo, resumoOrigem } = pickResumo(candidatosResumo)
    grupos.push({
      corretor_id,
      corretora_nome: resumo.corretora_nome || contatos[0]?.corretora_nome || '—',
      resumo,
      resumoOrigem,
      contatos,
    })
  }

  grupos.sort((a, b) => compareLigacaoDesc(a.resumo, b.resumo))
  return grupos
}
